import { Component } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { cx } from 'ramda-extension';
import { connect } from 'react-redux';

import Staple from '@gef-ui/components/atoms/SvgIcon/Icons/Staple';
import Upload from '@gef-ui/components/atoms/SvgIcon/Icons/Upload';
import Text from '@gef-ui/components/atoms/Text';
import { getLabelMessage } from '@gef-ui/localization/messages';

import FileGroup from 'components/atoms/FileGroup';
import sellerRequestAttachments from 'features/views/newTaskSellerRequest/attachments';

import processNewFiles from './utils';

let index = 0;
// Gets files from drop event's dataTransfer property
const getFilesFromDropEvent = (e) => {
	let files = [];
	if (e.dataTransfer.items) {
		for (const item of e.dataTransfer.items) {
			if (item.kind === 'file') {
				files.push(item.getAsFile());
			}
		}
	} else {
		files = e.dataTransfer.files;
	}
	return files;
};
class FileUpload extends Component {
	static defaultProps = {
		disabled: false,
		acceptedFileTypes: /.*/,
		multiple: false,
		maxFilesCount: 10,
		maxFileNameSize: 50,
	};

	state = {
		hover: false,
		files: [],
	};

	// When unmounting - sellerRequest-attachments is reseted, files and errors also
	componentWillUnmount() {
		this.props.setFiles([]);
	}

	id = `fileUpload_${index++}`;

	setFilesToState = (files) => {
		const {
			acceptedFileTypes,
			disabled,
			maxSize,
			maxSizeOverall,
			maxFilesCount,
			maxFileNameSize,
			setFiles,
			setUploadErrors,
		} = this.props;

		if (disabled || files.length === 0) {
			return;
		}

		const options = { acceptedFileTypes, maxSize, maxSizeOverall, maxFilesCount, maxFileNameSize };
		const { combinedFiles, errors } = processNewFiles(files, this.props.files, options);

		if (!isEmpty(errors)) {
			return setUploadErrors(errors.map((error) => getLabelMessage(`fileUpload:${error.type}`, error)));
		}

		setFiles(combinedFiles);
	};

	handleRemoveFile = ({ name }) => {
		const files = this.props.files.filter((file) => file.name !== name);
		this.props.setFiles(files);
	};

	handleFileChange = (e) => {
		this.setFilesToState(Array.from(e.target.files));
		e.target.value = null; // Important! without this same files wont trigger on change event on Input filed
	};

	handleFileDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.setState(() => ({ hover: false }));
		this.setFilesToState(getFilesFromDropEvent(e));
	};

	handleOnDragOver = (e) => {
		e.preventDefault(); // onDragOver Event is preventing onDrop Event from executing
		const files = getFilesFromDropEvent(e);

		if (files.length > 0 && !this.state.hover) {
			this.setState(() => ({ hover: true }));
		}
	};

	handleDragLeave = () => {
		this.setState(() => ({ hover: false }));
	};

	render() {
		const { accept, disabled, multiple, files } = this.props;
		const { hover } = this.state;

		return (
			<div
				className={cx('FileUpload', { 'FileUpload--shake': hover })}
				onDrop={this.handleFileDrop}
				onDragOver={this.handleOnDragOver}
				onDragLeave={this.handleDragLeave}
			>
				<div className="FileUpload__header">
					<Upload className="FileUpload__upload-icon" />
					<Text
						type={Text.typeConstants.SECONDARY}
						weight={Text.weightConstants.BLACK}
						className="FileUpload__header-name"
					>
						{getLabelMessage('fileUpload:dragFiles')}
					</Text>
					<div className="FileInput">
						<input
							className="FileInput-input"
							type="file"
							accept={accept}
							onChange={this.handleFileChange}
							disabled={disabled}
							multiple={multiple}
							id={this.id}
						/>
						<label className="FileInput-control" htmlFor={this.id}>
							<div className="Link">
								<Staple className="FileUpload__staple-icon" />
								{getLabelMessage('fileUpload:uploadFile')}
							</div>
						</label>
					</div>
				</div>
				{files.length > 0 && (
					<div className="FileUpload__fileGroupContainer">
						<FileGroup files={files} onRemoveFile={this.handleRemoveFile} />
					</div>
				)}
			</div>
		);
	}
}

FileUpload.propTypes = {
	accept: PropTypes.string,
	acceptedFileTypes: PropTypes.instanceOf(RegExp),
	disabled: PropTypes.bool,
	files: PropTypes.array,
	maxFileNameSize: PropTypes.number,
	maxFilesCount: PropTypes.number,
	maxSize: PropTypes.number,
	maxSizeOverall: PropTypes.number,
	multiple: PropTypes.bool,
	setFiles: PropTypes.func,
	setUploadErrors: PropTypes.func,
};

export default connect(
	(state) => ({
		files: sellerRequestAttachments.selectors.getFiles(state),
	}),
	{
		setFiles: sellerRequestAttachments.actions.setFiles,
		setUploadErrors: sellerRequestAttachments.actions.setUploadErrors,
	},
)(FileUpload);
